export const TOOLS_EN = [
  {
    slug: "onix-messenger",
    num: "01",
    name: "ONIX MESSENGER",
    pillar: "COMMUNICATE",
    title: "Private conversations, on your terms.",
    short:
      "A communication space of your own: what is said, who says it and how work is coordinated stays inside, protected and under your control.",
    description:
      "ONIX Messenger gives your organisation a private, protected communication space. Every person joins with a verified identity, conversations and files travel encrypted, and you decide who gets in, from which devices and for how long. When something changes — a departure, a lost device, a project that ends — you can suspend or revoke access instantly.",
    capabilities: [
      "Cryptographic identity",
      "End-to-end encryption subject to version and audit",
      "Groups and files",
      "Device governance",
      "Metadata minimisation",
      "Dedicated deployments when risk requires it",
    ],
    limit:
      "Let's be honest: encryption cannot protect a device that is already compromised, stop a participant from taking a screenshot, or recover information that has already left. The specific cryptographic properties are verified per version.",
  },
  {
    slug: "onix-vision",
    num: "02",
    name: "ONIX VISION",
    pillar: "OBSERVE",
    title: "Your cameras see a lot. ONIX Vision helps you understand it.",
    short:
      "Turn your organisation's cameras and authorised sessions into understandable events, well-ordered investigations and evidence that holds up.",
    description:
      "ONIX Vision turns hours of video into information you can actually work with: it detects events, prioritises them by risk and lays them out on a clear timeline. When something needs investigating, every finding is documented with its origin, its date and its chain of custody, so the evidence holds up when it matters most.",
    capabilities: [
      "Events and risk",
      "Cameras and timeline",
      "Investigations and cases",
      "Secure sessions",
      "Evidence Vault",
      "Integrity, retention and chain of custody",
      "Rules, zones and thresholds",
    ],
    limit:
      "Automated detection flags and prioritises; people draw the conclusions. Accuracy and suitability must be validated in each real scenario before decisions rely on it.",
  },
  {
    slug: "onix-tactical-graph",
    num: "03",
    name: "ONIX TACTICAL GRAPH",
    pillar: "INVESTIGATE",
    title: "See the connections a list will never show you.",
    short:
      "A visual environment where people, organisations, places and indicators connect across graphs and maps, so your team investigates with context.",
    description:
      "ONIX Tactical Graph helps your team think visually: it connects people, organisations, assets, incidents and locations from authorised sources, and places them on graphs and maps where relationships and timing make sense at a glance. Hypotheses, notes and evidence stay organised within each case, ready to be shared with control.",
    capabilities: [
      "Entities and links",
      "Maps and layers",
      "Temporality",
      "Confidence levels",
      "Authorised sources",
      "Hypotheses, notes, evidence, tasks and controlled exports",
    ],
    limit:
      "Two elements appearing connected proves neither causality nor identity. Every data point keeps its provenance, date, legitimate basis and confidence level — and must be read that way.",
  },
  {
    slug: "onix-malware-static-analyzer",
    num: "04",
    name: "ONIX MALWARE STATIC ANALYZER",
    pillar: "ANALYSE",
    title: "Understand a suspicious file without having to run it.",
    short:
      "Static, preferably offline inspection: hashes, structure, indicators and risk signals from a sample, without deliberately executing it.",
    description:
      "When a suspicious file arrives, the first question is what it is and what it contains. ONIX Malware Static Analyzer examines it without deliberately executing it: it identifies the real file type, extracts hashes, strings, domains and paths, measures entropy and gathers the findings into a technical report ready to plug into your cases or your SIEM.",
    capabilities: [
      "Real type identification",
      "Signatures and entropy",
      "Controlled extraction",
      "Strings, domains and paths",
      "Archive bomb and traversal mitigation",
      "Technical report",
      "Case and SIEM integration",
    ],
    limit:
      "Static analysis tells you what the file is, not everything it would do when executed. It does not replace an authorised sandbox, reverse engineering or forensic analysis when the case calls for them.",
  },
  {
    slug: "onix-autosoc-siem",
    num: "05",
    name: "ONIX AUTOSOC / SIEM",
    pillar: "PROTECT",
    title: "Less noise, better decisions for your security team.",
    short:
      "Centralise your organisation's telemetry, separate the urgent from the irrelevant and coordinate the response with a full record of every step.",
    description:
      "ONIX AutoSOC brings the signals from your systems into one place, correlates them and prioritises them so your team spends its time on what truly matters. Cases are managed with playbooks, automation always works under human supervision, and every action is recorded for audit and executive reporting.",
    capabilities: [
      "Event ingestion",
      "Rules and behaviour",
      "Temporal correlation",
      "Triage and enrichment",
      "Cases and playbooks",
      "Executive reporting",
      "Assisted automation under supervision",
    ],
    limit:
      "Real coverage depends on which sources are connected, the agreed scope and the acceptance tests. We would rather define that with you than promise total visibility.",
  },
  {
    slug: "sensorizacion-ciberdefensa",
    num: "06",
    name: "ONIX SENSORISATION & CYBER DEFENCE",
    pillar: "PROTECT",
    title: "Know what you have, how it is doing and where you are exposed.",
    short:
      "Asset inventory, telemetry, access control and hardening of networks, devices, services and cloud: visibility first, protection second.",
    description:
      "You cannot protect what you cannot see. This capability helps your organisation inventory its assets, measure its exposure and reinforce whatever needs it: segmentation, secure remote access, perimeter protection, WAF, VPN, hardening, backups and periodic reviews. From the initial assessment to the day-to-day managed service.",
    capabilities: [
      "External surface",
      "Inventory and status",
      "Telemetry",
      "Segmentation",
      "Remote access",
      "Perimeter protection",
      "Controlled deception",
      "WAF, VPN, hardening, backups and periodic reviews",
    ],
    limit:
      "Aligning with ENS, ISO 27001 or other frameworks requires formal scope, implementation, evidence and audit. We do not declare certifications that are not accredited.",
  },
  {
    slug: "dwi-onix",
    num: "07",
    name: "DWI ONIX",
    pillar: "INVESTIGATE",
    title: "What circulates about your organisation, turned into intelligence.",
    short:
      "Collect and connect authorised sources — including the permitted dark web — and turn them into indicators, alerts and traceable investigations.",
    description:
      "DWI ONIX watches what appears about your organisation across authorised corpora: URLs, permitted Onion services, documents, chats, feeds and data you provide. It extracts domains, emails, exposed credentials, CVEs and relationships; detects typosquatting; and turns every match into an alert or a case with its source, date, hash and confidence level. Always with human validation.",
    capabilities: [
      "Extraction of domains, emails, IPs, CVEs, hashes, files, accounts and relationships",
      "Trackers",
      "Sets and rules",
      "Typosquatting",
      "Human validation",
      "Hypotheses and cases",
      "Evidence with source, date, hash, confidence and limitations",
      "Assessment, monitoring, dedicated instance and intelligence service",
    ],
    limit:
      "We search the corpus that has been collected, imported or connected: nobody can promise universal access to the entire dark web, infallible attribution or content removal. Every search requires purpose, authorisation and permitted sources.",
  },
];

export const PILLARS_EN = [
  { num: "01", name: "COMMUNICATE", desc: "Let your team talk, share and coordinate without exposing anything." },
  { num: "02", name: "OBSERVE", desc: "See what happens across video, network, endpoints, services and domains." },
  { num: "03", name: "ANALYSE", desc: "Make sense of events, malware, relationships and behaviour." },
  { num: "04", name: "PROTECT", desc: "Detect, control access, segment and respond in time." },
  { num: "05", name: "INVESTIGATE", desc: "Organise cases, graphs, timelines, indicators and notes." },
  { num: "06", name: "EVIDENCE", desc: "Preserve the integrity, custody and audit trail of every finding." },
  { num: "07", name: "DIRECT", desc: "Decide with clear dashboards, alerts, reports and tasks." },
];

export const PRODUCT_DISCLAIMER_EN =
  "Availability, scope, controls and final properties are subject to contract, version, integration and acceptance tests.";
